import aiLogger from '../components/ai-logger';

/**
 * Handlers para eventos SSE do backend de IA.
 *
 * O protocolo de streaming usa prefixos de linha:
 *   [PROGRESS]{json}   — progresso de etapas/chunks
 *   [TOOL_CALL]{json}  — execução de ferramentas (doc generation)
 *   [TOOL_PROGRESS]{json}[/TOOL_PROGRESS] — progresso inline de tools (ask_questions, thinking)
 *   [HTML_MODE]         — sinaliza conteúdo HTML
 *   [CONTENT_START]     — marca início do conteúdo real
 *   [ERROR]msg          — erro (lança exceção)
 *   (texto)             — linhas de conteúdo após CONTENT_START
 */
export interface SSEStreamHandlers {
    /** Chamado para cada evento [PROGRESS]{json} */
    onProgress?: (data: any) => void;
    /** Chamado para cada evento [TOOL_CALL]{json} */
    onToolCall?: (data: any) => void;
    /** Chamado quando [CONTENT_START] é recebido */
    onContentStart?: () => void;
    /** Chamado para cada linha de conteúdo (acumulado) */
    onContentChunk?: (fullContent: string) => void;
    /** Chamado quando [HTML_MODE] é recebido */
    onHtmlMode?: () => void;
    /** Chamado para cada evento [TOOL_PROGRESS]{json}[/TOOL_PROGRESS] (ask_questions, thinking inline) */
    onToolProgress?: (data: any) => void;
    /** Chamado quando [ATHENA_SEARCH_STATUS] status [/ATHENA_SEARCH_STATUS] é recebido */
    onSearchStatus?: (status: string) => void;
    /** Chamado quando [ATHENA_ERROR] é recebido no stream */
    onAthenaError?: (message: string) => void;
}

/**
 * Lê um stream SSE do backend de IA, parseando o protocolo de prefixos.
 *
 * Retorna o conteúdo completo acumulado após [CONTENT_START].
 * Lança Error se [ERROR] for recebido no stream.
 *
 * Uso:
 * ```ts
 * const content = await readSSEStream(response, {
 *     onProgress: (data) => updateUI(data),
 *     onContentChunk: (full) => preview.innerHTML = full,
 * });
 * ```
 */
export async function readSSEStream(response: Response, handlers: SSEStreamHandlers = {}): Promise<string> {
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let fullContent = '';
    let contentStarted = false;
    let buffer = '';

    let pendingToolProgress = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 1. Detectar tags de sinalização atômica no buffer (sem depender de \n)
        if (!contentStarted && buffer.includes('[CONTENT_START]')) {
            const idx = buffer.indexOf('[CONTENT_START]');
            contentStarted = true;
            if (handlers.onContentStart) handlers.onContentStart();
            buffer = buffer.substring(idx + '[CONTENT_START]'.length);
        }

        if (buffer.includes('[HTML_MODE]')) {
            const idx = buffer.indexOf('[HTML_MODE]');
            if (handlers.onHtmlMode) handlers.onHtmlMode();
            buffer = buffer.substring(0, idx) + buffer.substring(idx + '[HTML_MODE]'.length);
        }

        // 2. Processar tags compostas ([TAG]...[/TAG]) que podem estar no buffer
        if (handlers.onToolProgress) {
            const combined = pendingToolProgress + buffer;
            pendingToolProgress = '';
            let remaining = combined;
            while (remaining.includes('[TOOL_PROGRESS]')) {
                const startTag = '[TOOL_PROGRESS]';
                const endTag = '[/TOOL_PROGRESS]';
                const startIdx = remaining.indexOf(startTag);
                const endIdx = remaining.indexOf(endTag, startIdx);

                if (endIdx === -1) {
                    pendingToolProgress = remaining.substring(startIdx);
                    remaining = remaining.substring(0, startIdx);
                    break;
                }

                const jsonStr = remaining.substring(startIdx + startTag.length, endIdx);
                try {
                    handlers.onToolProgress(JSON.parse(jsonStr));
                } catch (e) {
                    aiLogger.warn('[SSEStream] Failed to parse tool progress:', jsonStr);
                }
                remaining = remaining.substring(0, startIdx) + remaining.substring(endIdx + endTag.length);
            }
            buffer = remaining;
        }

        if (handlers.onSearchStatus) {
            let searchIdx;
            while ((searchIdx = buffer.indexOf('[ATHENA_SEARCH_STATUS]')) !== -1) {
                const endTag = '[/ATHENA_SEARCH_STATUS]';
                const endIdx = buffer.indexOf(endTag, searchIdx);
                if (endIdx === -1) break;

                const status = buffer.substring(searchIdx + '[ATHENA_SEARCH_STATUS]'.length, endIdx);
                handlers.onSearchStatus(status);
                buffer = buffer.substring(0, searchIdx) + buffer.substring(endIdx + endTag.length);
            }
        }

        if (handlers.onAthenaError && buffer.includes('[ATHENA_ERROR]')) {
            const errorIdx = buffer.indexOf('[ATHENA_ERROR]');
            const errorMsg = buffer.substring(errorIdx + '[ATHENA_ERROR]'.length).trim();
            handlers.onAthenaError(errorMsg);
            return fullContent;
        }


        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, '');
            if (line.startsWith('[PROGRESS]')) {
                if (handlers.onProgress) {
                    try { handlers.onProgress(JSON.parse(line.substring('[PROGRESS]'.length))); } catch (e) {}
                }
            } else if (line.startsWith('[TOOL_CALL]')) {
                if (handlers.onToolCall) {
                    try { handlers.onToolCall(JSON.parse(line.substring('[TOOL_CALL]'.length))); } catch (e) {}
                }
            } else if (line.startsWith('[ERROR]')) {
                throw new Error(line.substring('[ERROR]'.length));
            } else if (contentStarted) {
                fullContent += line + '\n';
                if (handlers.onContentChunk) handlers.onContentChunk(fullContent);
            }
        }


        if (contentStarted && buffer.length > 0) {

            if (!buffer.startsWith('[') || buffer.includes(' ')) {
                const currentChunk = fullContent + buffer;
                if (handlers.onContentChunk) handlers.onContentChunk(currentChunk);
            }
        }
    }


    if (buffer.length > 0) {
        if (!contentStarted && buffer.trim().length > 0) {
            aiLogger.warn('[SSEStream] Finalizado sem [CONTENT_START], usando buffer como fallback.');
            contentStarted = true;
        }
        
        if (contentStarted) {
            fullContent += buffer;
        }
    }

    return fullContent.trim();
}
